/**
 * Core domain types.
 *
 * Ported from the single-file prototype's in-memory object shapes. Field names
 * are kept close to the prototype so the migration is auditable, but anything
 * that was a browser-only convenience (base64 image blobs on rows, `_id` local
 * keys) is replaced by a server-side equivalent.
 */

/** System access level. One per user; drives both routing and permissions. */
export const ACCESS = {
  REQUESTOR: 'REQUESTOR',
  MANAGER_APPROVER: 'MANAGER_APPROVER',
  LEAD_APPROVER: 'LEAD_APPROVER',
  EXECUTIVE_APPROVER: 'EXECUTIVE_APPROVER',
  FINANCE_REVIEW: 'FINANCE_REVIEW',
  MAIN_ACCESS: 'MAIN_ACCESS',
} as const;

export type AccessLevel = (typeof ACCESS)[keyof typeof ACCESS];

/**
 * Access levels Round 2 retired, and what they became.
 *
 * Finance Approver and Treasury were separate desks until the three finance
 * stages collapsed into one. Their holders were moved to FINANCE_REVIEW by
 * migration 0015, so no live row should carry either — but the values still
 * exist in the database enum, which Postgres cannot drop, and a row restored
 * from a backup taken before the migration would carry one. Reading is
 * therefore forgiving and writing is impossible: these are not in ACCESS, so
 * nothing can assign them again.
 */
export const RETIRED_ACCESS: Record<string, AccessLevel> = {
  FINANCE_APPROVER: ACCESS.FINANCE_REVIEW,
  TREASURY: ACCESS.FINANCE_REVIEW,
};

/**
 * Map whatever the database holds onto a level this code understands.
 *
 * A retired level becomes the one that replaced it. Anything unrecognised
 * becomes REQUESTOR — the least privilege available — because an access level
 * nobody can interpret must never be read as permission.
 */
export function normalizeAccessLevel(raw: string): AccessLevel {
  if (raw in ACCESS) return raw as AccessLevel;
  return RETIRED_ACCESS[raw] ?? ACCESS.REQUESTOR;
}

export const ACCESS_LABEL: Record<AccessLevel, string> = {
  REQUESTOR: 'Requestor',
  MANAGER_APPROVER: 'Manager Approver',
  LEAD_APPROVER: 'Lead Approver',
  EXECUTIVE_APPROVER: 'Executive Approver',
  FINANCE_REVIEW: 'Finance — Review & Approval',
  MAIN_ACCESS: 'Main Access',
};

/**
 * How many people may hold Finance access at once.
 *
 * Carried over from the prototype's three-Finance-Approver cap. Round 2 merged
 * review and approval into one role, so the cap now applies to that role —
 * which is the tighter reading, since a single holder both reviews and
 * releases payment.
 */
export const MAX_ACTIVE_FINANCE_HOLDERS = 3;

export const EMP_LEVELS = [
  'Rank & File',
  'Supervisory',
  'Managerial',
  'Lead / Director',
  'Executive',
] as const;
export type EmploymentLevel = (typeof EMP_LEVELS)[number];

export const PAYMENT_STATUSES = ['Pending', 'Released', 'Failed'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export interface User {
  id: string;
  empId: string;
  name: string;
  email: string;
  department: string;
  position: string;
  employmentLevel: EmploymentLevel;
  systemAccess: AccessLevel;
  /** Employee id of this user's manager, or null for the top of a branch. */
  reportsTo: string | null;
  active: boolean;
  profileComplete: boolean;
  separationDate?: string | null;
  deactivationReason?: string | null;
  /**
   * What this employee may claim in a calendar month, in the home currency.
   *
   * Finance-owned and **advisory**: it is shown to the reviewer beside the
   * claimed amount and never blocks a submission or a release. NULL means
   * nobody has set one, which is different from 0 ("may claim nothing").
   */
  monthlyAllowance?: number | null;
  /**
   * The employee's vendor code in SAP, carried onto the Finance export.
   * Finance-owned, and not a gate — a claim may be released without one.
   */
  sapVendorCode?: string | null;
}

export interface ExpenseCategory {
  id: string;
  name: string;
  active: boolean;
}

/**
 * One receipt line on a claim.
 *
 * `description` is a text snapshot of the category name at submit time —
 * renaming or deactivating a category must never rewrite historical claims.
 */
export interface ClaimRow {
  id: string;
  date: string; // YYYY-MM-DD
  receipt: string;
  company: string;
  tin: string;
  description: string;
  /** ALWAYS in the home currency. This is what the claim is worth. */
  amount: number;
  /** ISO 4217 code printed on the receipt. 'PHP' when it needed no conversion. */
  currency: string;
  /** The figure as printed, in `currency`. Equals `amount` for a peso receipt. */
  originalAmount: number | null;
  /**
   * The rate used to reach `amount`, frozen at submit. NULL for a peso receipt
   * — no conversion happened, and 1.0 would imply one did.
   */
  fxRate: number | null;
  vatStatus: 'Yes' | 'No';
  /** S3 object key for the receipt image, or null. Never a base64 blob. */
  imageKey: string | null;
  sourceName?: string | null;
}

export type ApprovalRole = 'Checked By' | 'Approved By';
export type ApprovalDecision = 'Checked' | 'Approved' | 'Disapproved' | null;

export interface ApprovalStep {
  role: ApprovalRole;
  userId: string;
  decision: ApprovalDecision;
  reason: string;
  ts: number | null;
}

/**
 * The claim lifecycle.
 *
 * Round 2 collapsed three finance stages into one: a claim leaves business
 * approval, waits at "For Finance Review", is taken by a Finance holder
 * ("Finance Reviewing"), and the single act of approving it also releases it.
 * `For Finance Approval`, `Finance Approving`, `For Treasury Release` and
 * `Treasury Processing` are gone — migration 0015 moved every claim out of
 * them. The database enum still lists them, because Postgres cannot remove an
 * enum value, but nothing writes them.
 */
export const CLAIM_STATUSES = [
  'For Checking',
  'For Approval',
  'Disapproved',
  'For Finance Review',
  'Finance Reviewing',
  'Released',
  // Terminal, administrative. A claim is never deleted; it is voided, with a
  // reason and an attributed actor, so the record survives the decision.
  'Voided',
] as const;
export type ClaimStatus = (typeof CLAIM_STATUSES)[number];

export interface AmountHistoryEntry {
  /**
   * `Final Approved Amount` is historic: it was written by the separate
   * Finance Approval stage that Round 2 retired. Existing claims still carry
   * entries labelled with it, so it is still read — it is simply never written
   * again.
   */
  field: 'Finance Reviewed Amount' | 'Final Approved Amount' | 'Released Amount';
  from: number | null;
  to: number;
  user: string;
  ts: number;
  reason: string;
}

export interface Claim {
  /** Human-facing request id, e.g. EXP-2026-00001. Also the primary key. */
  id: string;
  requesterId: string;
  /** Claim period as YYYY-MM. All receipt dates must fall inside it. */
  period: string;
  rows: ClaimRow[];
  status: ClaimStatus;
  approvalChain: ApprovalStep[];
  claimedAmount: number;

  financeReviewerId: string | null;
  financeReviewedAmount: number | null;
  financeReviewRemark: string;
  financeReviewedAt: number | null;

  /**
   * The four fields below belonged to the separate Finance Approval and
   * Treasury stages. Round 2 merged those into the review stage, so they are
   * read (historic claims have them) and no longer written. `financeApprovedAt`
   * is still set, because a claim is still approved — by the same person, at
   * the same moment they release it.
   */
  financeApproverId: string | null;
  financeApprovalDecision: 'Approved' | 'Disapproved' | null;
  financeApprovalReason: string;
  financeApprovedAt: number | null;

  finalApprovedAmount: number | null;

  treasuryUserId: string | null;
  releasedAmount: number | null;
  releaseDate: number | null;
  paymentReference: string;
  paymentStatus: PaymentStatus | '';

  /**
   * Whether Finance has the paper receipts (Round 2, item 7).
   *
   * Tracked on the claim rather than inferred, because it is a fact about the
   * physical world that only Finance can assert. It gates nothing: a claim can
   * be approved and released with the originals still outstanding, which is
   * how it already worked and what Finance asked to keep.
   */
  originalsReceivedAt: number | null;
  originalsReceivedBy: string | null;

  amountHistory: AmountHistoryEntry[];
  disapprovalReason: string;
  voidedAt: number | null;
  voidedBy: string | null;
  voidReason: string;
  version: number;

  createdAt: number;
  updatedAt: number;
}

export interface AuditEntry {
  id: string;
  ts: number;
  user: string;
  action: string;
  requestId: string;
  details: string;
}

export interface Notification {
  id: string;
  userId: string;
  text: string;
  ts: number;
  read: boolean;
}

/**
 * An exchange rate Finance maintains: home-currency units per one unit of
 * `currency`. There is exactly one current rate per currency.
 */
export interface FxRate {
  currency: string;
  rate: number;
  note: string;
  updatedAt: number;
  updatedByName: string;
}
